/**
 * useAdminOrchestratorHub
 *
 * US-24-GR-11 — wraps the SignalR connection to /hubs/notifications,
 * listens for ReceiveAdminOrchestratorEvent, and maintains a rolling 200-event buffer.
 *
 * Group join (Admin_Orchestrator) is handled server-side by the hub's
 * OnConnectedAsync when the user has the Admin role claim.
 */

import { useEffect, useRef, useState, useCallback, startTransition } from 'react';
import * as signalR from '@microsoft/signalr';
import { useAuth } from '../auth/AuthContext';
import { isDevModeActive } from '../utils/devModeEnabled';

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export type EventType =
  | 'Progress'
  | 'Blocked'
  | 'Completed'
  | 'Failed'
  | 'ActionRequired'
  | 'RunComplete'
  | 'HealthCheck'
  | 'DlqAlert'
  | 'ThresholdAlert';

export type Severity = 'info' | 'warn' | 'error';

export type ConnectionState = 'Connected' | 'Connecting' | 'Disconnected';

export interface AdminOrchestratorEvent {
  /** Unique ID assigned client-side for React keys */
  _id: string;
  Timestamp: string;
  EventType: EventType;
  JobId: string | null;
  ProfileId: string | null;
  CardId: string | null;
  Summary: string;
  Severity: Severity;
  Metadata: Record<string, string>;
}

// ---------------------------------------------------------------------------
// Constants
// ---------------------------------------------------------------------------

const BUFFER_CAP = 200;
// Absolute host in deployed builds (the SWA serves no hub — a relative path
// 405s at /negotiate and the tail reconnects forever); Vite proxy locally.
const HUB_URL = import.meta.env.VITE_NOTIFICATIONS_HUB_URL || '/hubs/notifications';

// ---------------------------------------------------------------------------
// Hook
// ---------------------------------------------------------------------------

interface UseAdminOrchestratorHubResult {
  events: AdminOrchestratorEvent[];
  status: ConnectionState;
  pause: () => void;
  resume: () => void;
}

let _idCounter = 0;
function nextId(): string {
  return `evt-${++_idCounter}`;
}

export function useAdminOrchestratorHub(): UseAdminOrchestratorHubResult {
  const { getAccessToken } = useAuth();
  const [events, setEvents] = useState<AdminOrchestratorEvent[]>([]);
  const [status, setStatus] = useState<ConnectionState>('Connecting');
  const pausedRef = useRef(false);
  const pendingRef = useRef<AdminOrchestratorEvent[]>([]);
  const connectionRef = useRef<signalR.HubConnection | null>(null);

  const addEvent = useCallback((raw: Omit<AdminOrchestratorEvent, '_id'>) => {
    const event: AdminOrchestratorEvent = { ...raw, _id: nextId() };

    if (pausedRef.current) {
      pendingRef.current = [event, ...pendingRef.current].slice(0, BUFFER_CAP);
      return;
    }

    setEvents((prev) => [event, ...prev].slice(0, BUFFER_CAP));
  }, []);

  const pause = useCallback(() => {
    pausedRef.current = true;
  }, []);

  const resume = useCallback(() => {
    pausedRef.current = false;
    if (pendingRef.current.length > 0) {
      setEvents((prev) => [...pendingRef.current, ...prev].slice(0, BUFFER_CAP));
      pendingRef.current = [];
    }
  }, []);

  useEffect(() => {
    let disposed = false;

    const connection = new signalR.HubConnectionBuilder()
      .withUrl(HUB_URL, {
        accessTokenFactory: async () => {
          const token = await getAccessToken();
          return token ?? '';
        },
      })
      .withAutomaticReconnect()
      .configureLogging(signalR.LogLevel.Warning)
      .build();

    connectionRef.current = connection;

    // The one place raw hub payloads become buffered events — extracted to a
    // named handler so the devmode test seam below can invoke the exact same
    // code path a real 'ReceiveAdminOrchestratorEvent' invocation would.
    const handleIncomingEvent = (data: Omit<AdminOrchestratorEvent, '_id'>) => {
      if (!disposed) addEvent(data);
    };

    connection.on('ReceiveAdminOrchestratorEvent', handleIncomingEvent);

    // Devmode-gated test seam (build-time stripped from prod builds — see
    // isDevModeActive()). Playwright has no way to synthesize SignalR
    // transport frames via page.route(), so this lets a live-E2E-style spec
    // inject events through the identical handler real hub traffic uses,
    // with no backend required.
    if (isDevModeActive()) {
      window.__adminHubTestEmit = handleIncomingEvent;
    }

    connection.onreconnecting(() => {
      if (!disposed) setStatus('Connecting');
    });

    connection.onreconnected(() => {
      if (!disposed) setStatus('Connected');
    });

    connection.onclose(() => {
      if (!disposed) setStatus('Disconnected');
    });

    startTransition(() => { setStatus('Connecting'); });
    connection
      .start()
      .then(() => {
        if (!disposed) setStatus('Connected');
      })
      .catch(() => {
        if (!disposed) setStatus('Disconnected');
      });

    return () => {
      disposed = true;
      connectionRef.current = null;
      if (window.__adminHubTestEmit === handleIncomingEvent) {
        delete window.__adminHubTestEmit;
      }
      void connection.stop();
    };
  }, [getAccessToken, addEvent]);

  return { events, status, pause, resume };
}
